// ============================================================
// TaskFlow OS - Activity Log Page (Git-style UI)
// ============================================================

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Header from '../components/layout/Header';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/authStore';
import type { EventLogEntry } from '../lib/types';
import { EVENT_TYPE_LABELS } from '../lib/constants';
import {
  IconGitCommit, IconUser, IconMapPin, IconGlobe,
  IconRefresh, IconSearch, IconActivity, IconChevronDown,
} from '../icons/SvgIcons';

const ActivityLog: React.FC = () => {
  const { user } = useAuthStore();
  const [events, setEvents] = useState<EventLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const PAGE_SIZE = 30;

  const fetchEvents = async (reset = false) => {
    setLoading(true);
    const currentPage = reset ? 0 : page;
    const from = currentPage * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    let query = supabase
      .from('event_log')
      .select('*')
      .order('created_at', { ascending: false })
      .range(from, to);

    if (filter !== 'all') {
      query = query.eq('event_type', filter);
    }

    const { data } = await query;

    if (data) {
      if (reset) {
        setEvents(data as EventLogEntry[]);
        setPage(0);
      } else {
        setEvents((prev) => [...prev, ...(data as EventLogEntry[])]);
      }
      setHasMore(data.length === PAGE_SIZE);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchEvents(true);
  }, [filter]);

  const loadMore = () => {
    setPage((p) => p + 1);
    fetchEvents();
  };

  // Group events by date
  const groupedEvents = events.reduce<Record<string, EventLogEntry[]>>((acc, event) => {
    const date = new Date(event.created_at).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    if (!acc[date]) acc[date] = [];
    acc[date].push(event);
    return acc;
  }, {});

  const getEventColor = (type: string): string => {
    if (type.includes('created') || type.includes('approved')) return '#10b981';
    if (type.includes('deleted') || type.includes('rejected')) return '#ef4444';
    if (type.includes('updated') || type.includes('changed')) return '#3b82f6';
    if (type.includes('ai_')) return '#8b5cf6';
    return '#6b7280';
  };

  return (
    <>
      <Header
        title="アクティビティログ"
        subtitle="すべての操作を時系列で確認"
        actions={
          <button onClick={() => fetchEvents(true)} className="glass-btn flex items-center gap-2 text-sm">
            <IconRefresh size={14} />
            更新
          </button>
        }
      />

      <div className="p-6">
        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex gap-1 p-1 rounded-xl bg-white/5">
            {[
              { value: 'all', label: 'すべて' },
              { value: 'node_status_changed', label: 'ステータス変更' },
              { value: 'workflow_created', label: 'ワークフロー' },
              { value: 'project_created', label: 'プロジェクト' },
              { value: 'member_added', label: 'メンバー' },
            ].map((f) => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  filter === f.value
                    ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20'
                    : 'text-white/40 hover:text-white/60'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Git-style Timeline */}
        <div className="relative">
          {Object.entries(groupedEvents).map(([date, dateEvents], groupIdx) => (
            <div key={date} className="mb-8">
              {/* Date header */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: groupIdx * 0.05 }}
                className="flex items-center gap-3 mb-4"
              >
                <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                  <IconGitCommit size={14} className="text-emerald-400" />
                </div>
                <span className="text-sm font-semibold text-white/80">{date}</span>
                <span className="text-xs text-white/30">{dateEvents.length} 件のイベント</span>
              </motion.div>

              {/* Events */}
              <div className="ml-4 border-l-2 border-white/5 pl-6 space-y-1">
                {dateEvents.map((event, eventIdx) => {
                  const color = getEventColor(event.event_type);
                  return (
                    <motion.div
                      key={event.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: (groupIdx * dateEvents.length + eventIdx) * 0.02 }}
                      className="relative flex items-start gap-4 p-3 -ml-[33px] rounded-xl hover:bg-white/[0.02] transition-colors group"
                    >
                      {/* Timeline dot */}
                      <div
                        className="w-4 h-4 rounded-full shrink-0 mt-1 border-2 bg-gray-950"
                        style={{ borderColor: color }}
                      />

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-xs text-white/80">
                              <span className="font-semibold text-white">
                                {event.actor_display_name || '不明なユーザー'}
                              </span>
                              {' が '}
                              <span
                                className="font-medium"
                                style={{ color }}
                              >
                                {EVENT_TYPE_LABELS[event.event_type] || event.event_type}
                              </span>
                              {' を実行'}
                            </p>

                            {/* Payload details */}
                            {event.payload && Object.keys(event.payload).length > 0 && (
                              <div className="mt-1 p-2 rounded-lg bg-white/[0.02] border border-white/5 max-w-md">
                                {Object.entries(event.payload).slice(0, 4).map(([key, value]) => (
                                  <div key={key} className="flex gap-2 text-[10px]">
                                    <span className="text-white/30 min-w-[60px]">{key}:</span>
                                    <span className="text-white/50 truncate">{String(value)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <span className="text-[10px] text-white/20 shrink-0">
                            {new Date(event.created_at).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                          </span>
                        </div>

                        {/* Metadata row */}
                        <div className="flex items-center gap-4 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          {event.ip_address && (
                            <span className="flex items-center gap-1 text-[10px] text-white/20">
                              <IconGlobe size={10} /> {event.ip_address}
                            </span>
                          )}
                          {event.location && (
                            <span className="flex items-center gap-1 text-[10px] text-white/20">
                              <IconMapPin size={10} /> {event.location}
                            </span>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Load more */}
          {hasMore && events.length > 0 && (
            <div className="text-center mt-4">
              <button
                onClick={loadMore}
                disabled={loading}
                className="glass-btn text-sm flex items-center gap-2 mx-auto"
              >
                <IconChevronDown size={14} />
                {loading ? '読み込み中...' : 'さらに読み込む'}
              </button>
            </div>
          )}

          {events.length === 0 && !loading && (
            <div className="text-center py-20">
              <IconActivity size={32} className="text-white/10 mx-auto mb-3" />
              <p className="text-white/30 text-sm">アクティビティはまだありません</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ActivityLog;
